import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import myAxios from '../../_services/axiosilearnWrapper';
import Config from '../../_config/config';
import CertificateUnlock from '../../pages/my-certificates/CertificateUnlock';

const EVENT_NAME = 'credentials-may-have-changed';
const FOLLOWUP_DELAY_MS = 1800;

/**
 * App-wide watcher that turns newly-issued credentials into an in-app
 * unlock celebration. Any caller can fire `notifyCredentialMayHaveChanged()`
 * (attached to window for convenience) to trigger a check; the watcher
 * also polls on route changes for the cases where a player forgets to
 * signal explicitly.
 *
 * Only credentials issued *after this watcher mounted* are celebrated,
 * and each UUID can only celebrate once per session — so a returning
 * student doesn't get a confetti barrage of their entire history.
 */
function CredentialUnlockWatcher() {
  const history = useHistory();
  const [unlockCert, setUnlockCert] = useState(null);
  const sessionStartRef = useRef(new Date().toISOString());
  const celebratedUUIDsRef = useRef(new Set());
  const inFlightRef = useRef(false);
  const queuedRef = useRef(null);

  const check = useCallback(async () => {
    const enquiryId = localStorage.getItem('enquiryId');
    if (!enquiryId) return;
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      const res = await myAxios.get(
        `${Config.nodeSiteUrl}v1/credentials/my-certificates?enquiryId=${enquiryId}`,
      );
      if (res.data?.status !== 1) return;
      const since = sessionStartRef.current;
      const fresh = (res.data.data || []).find((c) => {
        if (!c.issuedDate) return false;
        if (celebratedUUIDsRef.current.has(c.credentialUUID)) return false;
        return new Date(c.issuedDate).toISOString() > since;
      });
      if (fresh) {
        celebratedUUIDsRef.current.add(fresh.credentialUUID);
        setUnlockCert(fresh);
      }
    } catch (err) {
      console.warn('[CredentialUnlockWatcher] check failed:', err?.message || err);
    } finally {
      inFlightRef.current = false;
    }
  }, []);

  // Listen for explicit "something just happened, look for a new credential"
  // events. Re-check after a short delay since the backend auto-issue runs
  // via setImmediate, so the credential row appears a beat after the
  // resource completion request returns.
  useEffect(() => {
    const handler = () => {
      check();
      if (queuedRef.current) clearTimeout(queuedRef.current);
      queuedRef.current = setTimeout(() => {
        queuedRef.current = null;
        check();
      }, FOLLOWUP_DELAY_MS);
    };
    window.addEventListener(EVENT_NAME, handler);
    return () => {
      window.removeEventListener(EVENT_NAME, handler);
      if (queuedRef.current) clearTimeout(queuedRef.current);
    };
  }, [check]);

  // Belt-and-suspenders: also peek whenever the URL changes. Cheap, and
  // catches paths where a player forgot to dispatch the event.
  useEffect(() => {
    const unlisten = history.listen(() => {
      check();
    });
    return unlisten;
  }, [history, check]);

  if (!unlockCert) return null;

  return (
    <CertificateUnlock
      cert={unlockCert}
      onClose={() => setUnlockCert(null)}
      onView={() => {
        setUnlockCert(null);
        history.push('/my-certificates');
      }}
    />
  );
}

// Convenience global so callers anywhere in the app can signal without
// importing the event name.
if (typeof window !== 'undefined') {
  window.notifyCredentialMayHaveChanged = () => {
    window.dispatchEvent(new CustomEvent(EVENT_NAME));
  };
}

export default CredentialUnlockWatcher;
